import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link, animateScroll as scroll, scroller } from 'react-scroll';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import CountUp from 'react-countup';
import { FaKey, FaGift, FaUtensils, FaUsers, FaBook, FaCode, FaUserTie, FaCertificate, FaTrophy, FaUserCircle, FaShareAlt, FaTwitter, FaWhatsapp, FaBars, FaTimes } from 'react-icons/fa';
import DDImg from './images/DD.png';
import { Helmet } from 'react-helmet';
import newsletters from './data/newsletters.json';
// import logo from './logo.svg'; // Replace with your logo if available

// --- Navigation Bar ---
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const isHome = location.pathname === '/';
  // Cierra el menú al navegar
  useEffect(() => { setMenuOpen(false); }, [location]);
  return (
    <nav
      className={
        'fixed top-0 left-0 w-full z-50 border-b border-gray-100 transition-all duration-300 ' +
        (scrolled ? 'bg-white/70 backdrop-blur-sm' : 'bg-white')
      }
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <RouterLink to="/" className="flex items-center gap-3 cursor-pointer group">
          <div className="h-10 w-10 rounded-full bg-gray-800/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
            <img src="/favicon.jpg" alt="Data Drivers Logo" className="h-8 w-8 object-contain" />
          </div>
          <div className="text-2xl font-bold tracking-tight text-blue-700 group-hover:text-blue-900 transition-colors">Data Drivers</div>
        </RouterLink>
        {/* Menú normal en desktop */}
        <div className="space-x-8 hidden md:flex">
          <RouterLink
            to="/about"
            className="cursor-pointer text-gray-700 hover:text-blue-700 font-medium transition-colors"
          >
            Sobre Nosotros
          </RouterLink>
          {isHome ? (
            ['Home', 'Eventos', 'Join'].map((item) => (
              <Link
                key={item}
                to={item.toLowerCase()}
                smooth={true}
                duration={500}
                offset={-70}
                className="cursor-pointer text-gray-700 hover:text-blue-700 font-medium transition-colors"
                activeClass="text-blue-700"
                spy={true}
              >
                {item}
              </Link>
            ))
          ) : (
            <RouterLink
              to="/"
              className="cursor-pointer text-gray-700 hover:text-blue-700 font-medium transition-colors"
            >
              Home
            </RouterLink>
          )}
          <RouterLink
            to="/merch"
            className="cursor-pointer text-gray-700 hover:text-blue-700 font-medium transition-colors"
          >
            Merch
          </RouterLink>
          <RouterLink
            to="/newsletter"
            className="cursor-pointer text-gray-700 hover:text-blue-700 font-medium transition-colors"
          >
            Newsletter
          </RouterLink>
        </div>
        {/* Menú hamburguesa en móvil */}
        <button
          className="md:hidden flex items-center text-3xl text-blue-700 focus:outline-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>
      {/* Menú desplegable móvil */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40 animate-fade-in" onClick={() => setMenuOpen(false)}>
          <div className="absolute top-0 right-0 w-64 h-full bg-white shadow-lg flex flex-col py-8 px-6 gap-6 animate-slide-in" onClick={e => e.stopPropagation()}>
            <RouterLink to="/about" className="text-lg font-medium text-gray-700 hover:text-blue-700" onClick={() => setMenuOpen(false)}>Sobre Nosotros</RouterLink>
            {isHome ? (
              ['Home', 'Eventos', 'Join'].map((item) => (
                <Link
                  key={item}
                  to={item.toLowerCase()}
                  smooth={true}
                  duration={500}
                  offset={-70}
                  className="text-lg font-medium text-gray-700 hover:text-blue-700 block"
                  activeClass="text-blue-700"
                  spy={true}
                  onClick={() => setMenuOpen(false)}
                >
                  {item}
                </Link>
              ))
            ) : (
              <RouterLink to="/" className="text-lg font-medium text-gray-700 hover:text-blue-700 block" onClick={() => setMenuOpen(false)}>Home</RouterLink>
            )}
            <RouterLink to="/merch" className="text-lg font-medium text-gray-700 hover:text-blue-700" onClick={() => setMenuOpen(false)}>Merch</RouterLink>
            <RouterLink to="/newsletter" className="text-lg font-medium text-gray-700 hover:text-blue-700" onClick={() => setMenuOpen(false)}>Newsletter</RouterLink>
          </div>
        </div>
      )}
    </nav>
  );
};

// --- Hero Section ---
const Hero = () => (
  <section
    id="home"
    className="relative min-h-screen flex items-center justify-center bg-black"
    style={{ minHeight: '80vh' }}
  >
    <img
      src={require('./images/IMG_3060(2).JPG')}
      alt="Clase Data Drivers"
      className="absolute inset-0 w-full h-full object-cover object-center brightness-75"
      style={{ zIndex: 1 }}
    />
    <div className="absolute inset-0 bg-black/60" style={{ zIndex: 2 }} />
    <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
      <motion.h1
        className="text-5xl md:text-7xl font-extrabold text-white drop-shadow-lg mb-6 text-center"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        Data Drivers Club
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl drop-shadow"
      >
        Impulsando el futuro con IA, programación y análisis de datos en la Universidad de Navarra.
      </motion.p>
    </div>
  </section>
);

// --- Inscription Notification ---
const InscriptionNotification = () => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-4xl mx-4">
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl shadow-2xl border-2 border-green-400 p-4 relative animate-slide-down">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-2 right-2 text-white/80 hover:text-white text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
          aria-label="Cerrar notificación"
        >
          ×
        </button>
        <div className="flex items-center gap-4 pr-8">
          <div className="bg-white/20 rounded-full p-3">
            <FaUsers className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-1">¡Plazo de Inscripción ABIERTO!</h3>
            <p className="text-sm text-green-100">
              Únete a Data Drivers UNAV hasta octubre 2025. ¡No te quedes fuera!
            </p>
          </div>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSet5SPOboLaO5nRJ6XUf5uADwXvyErTL-o1n7h1_HYc1shqeA/viewform?usp=dialog"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-green-600 px-4 py-2 rounded-full font-semibold hover:bg-green-50 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap"
          >
            ¡Inscríbete!
          </a>
        </div>
      </div>
    </div>
  );
};

// --- Promotional Banner ---
const PromotionalBanner = () => (
  <section className="py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-full p-2">
            <FaUsers className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold">¡Nuevo Evento Disponible!</h3>
            <p className="text-sm text-blue-100 hidden md:block">RAG: La IA que busca antes de hablar - con Brais Klein Otero</p>
            <p className="text-sm text-blue-100 md:hidden">RAG: La IA que busca antes de hablar</p>
          </div>
        </div>
        <Link
          to="eventos"
          smooth={true}
          duration={500}
          offset={-70}
          className="bg-white text-blue-700 px-6 py-2 rounded-full font-semibold hover:bg-blue-50 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap"
        >
          Ver Evento
        </Link>
      </div>
    </div>
  </section>
);

// --- About / Mission Section ---
const About = () => (
  <section id="about" className="py-10 bg-gray-50">
    {/* Eliminado el div vacío para reducir el espacio */}
  </section>
);

// --- Features/Benefits Section ---
const features = [
  {
    icon: (
      <FaBook className="w-10 h-10 mx-auto mb-4 text-blue-700" />
    ),
    title: 'Workshops y Sesiones Formativas',
    desc: 'Aprende de forma práctica sobre IA, programación y análisis de datos.'
  },
  {
    icon: (
      <FaCode className="w-10 h-10 mx-auto mb-4 text-blue-700" />
    ),
    title: 'Hackathons y Retos',
    desc: 'Pon a prueba tus habilidades en desafíos reales y colaborativos.'
  },
  {
    icon: (
      <FaUserTie className="w-10 h-10 mx-auto mb-4 text-blue-700" />
    ),
    title: 'Ponencias de Expertos',
    desc: 'Conecta con líderes de la industria y aprende de su experiencia.'
  }
];

const Features = () => (
  <section id="features" className="pt-6 pb-20 bg-white">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid md:grid-cols-3 gap-10">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.7 }}
            className="bg-gray-50 rounded-lg p-8 text-center border border-gray-100"
          >
            {f.icon}
            <h3 className="text-xl font-semibold mb-2 text-gray-900">{f.title}</h3>
            <p className="text-gray-600 text-base">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const MemberBenefits = () => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  return (
    <section ref={ref} className="py-20 bg-blue-50 border-t border-b border-blue-100">
      <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-12">
        <div className="flex-1 flex flex-col items-center md:items-start">
          <h2 className="text-3xl font-bold text-blue-700 mb-4 text-center md:text-left">Beneficios para Miembros</h2>
          <ul className="space-y-4 text-lg">
            <li className="flex items-center gap-3">
              <FaKey className="w-7 h-7 text-blue-700" />
              Licencias de software premium (Datacamp, IBM, etc.)
            </li>
            <li className="flex items-center gap-3">
              <FaGift className="w-7 h-7 text-blue-700" />
              Regalos y sorteos exclusivos
            </li>
            <li className="flex items-center gap-3">
              <FaUtensils className="w-7 h-7 text-blue-700" />
              Comida y eventos sociales
            </li>
            <li className="flex items-center gap-3">
              <FaUsers className="w-7 h-7 text-blue-700" />
              Networking y acceso a ponentes y empresas
            </li>
            <li className="flex items-center gap-3">
              <FaCertificate className="w-7 h-7 text-blue-700" />
              Convalidación de ECTS por participación activa
            </li>
            <li className="flex items-center gap-3">
              <FaUsers className="w-7 h-7 text-blue-700" />
              Comunidad internacional con miembros de más de 10 países
            </li>
          </ul>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="bg-white rounded-2xl shadow-lg px-10 py-8 flex flex-col items-center">
            <span className="text-5xl md:text-6xl font-extrabold text-blue-700 mb-2">
              <CountUp end={inView ? 50 : 0} duration={2.5} suffix="+" />
            </span>
            <span className="text-lg text-gray-700 font-medium">Miembros en el club</span>
            <span className="text-sm text-blue-600 font-medium mt-2">de más de 10 países</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Gallery Section ---
const galleryImages = [
  { src: require('./images/IMG_3169.JPG'), alt: 'IMG_3169.JPG' },
  { src: require('./images/IMG_7623(1).JPG'), alt: 'IMG_7623(1).JPG' },
  { src: require('./images/IMG_7641.JPG'), alt: 'IMG_7641.JPG' },
  { src: require('./images/IMG_7692(1).JPG'), alt: 'IMG_7692(1).JPG' },
  { src: require('./images/IMG_7628.JPG'), alt: 'IMG_7628.JPG' },
  { src: require('./images/IMG_1067(1).jpg'), alt: 'IMG_1067(1).jpg' },
  { src: require('./images/img1.jpg'), alt: 'img1' },
  { src: require('./images/img2.jpg'), alt: 'img2' },
  { src: require('./images/Copy of IMG_0247.JPG'), alt: 'Copy of IMG_0247.JPG' },
  { src: require('./images/Copy of IMG_0232.jpg'), alt: 'Copy of IMG_0232.jpg' },
  // Puedes añadir más imágenes aquí
];

const Gallery = () => {
  // Detectar si es móvil
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const imagesToShow = isMobile ? galleryImages.slice(0, 3) : galleryImages;
  // Bloquear clic derecho y arrastre en imágenes
  const preventDefault = e => e.preventDefault();
  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Galería</h2>
        <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4">
          {imagesToShow.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="break-inside-avoid"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full rounded-lg border border-blue-100 mb-2 object-cover select-none pointer-events-none"
                onContextMenu={preventDefault}
                onDragStart={preventDefault}
                draggable={false}
                tabIndex={-1}
                aria-hidden="true"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Updated Events Section with Photos ---
const events = [
  {
    title: 'RAG: La IA que busca antes de hablar',
    ponente: 'Brais Klein Otero',
    date: '29 de septiembre 2025',
    modalidad: 'Online',
    img: require('./images/Modern black & purple professional game audio company promo instagram post (portrait).png'),
    destacado: true, // Marcar como evento destacado
  },
  {
    title: 'MiniReto Isla Sicilia x Data Drivers',
    desc: 'Desafío analítico real aplicado al ámbito empresarial. Colaboración con Isla Sicilia y Pallapizza.',
    date: 'Abril 2025',
    img: require('./images/event_isla_sicilia.jpg'),
  },
  {
    title: 'Sesión Formativa: Python, NumPy, Pandas y Matplotlib',
    desc: 'Consolidando competencias en análisis de datos moderno.',
    date: 'Marzo 2025',
    img: require('./images/event_python.jpg'),
  },
  {
    title: 'AI & Mainframe con IBM',
    desc: 'Sesión con Rubén Crespo, IBM Champion 2025, sobre mainframe, IA y cifrado cuántico.',
    date: 'Febrero 2025',
    img: require('./images/event_ibm.jpg'),
  },
];

const Events = () => (
  <section id="eventos" className="py-20 bg-gray-50">
    <div className="max-w-5xl mx-auto px-6">
      <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Eventos Recientes</h2>
      <div className="grid md:grid-cols-3 gap-8">
        {events.map((e, i) => (
          <motion.div
            key={e.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.7 }}
            className={`${
              e.destacado 
                ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 shadow-lg' 
                : 'bg-white border border-gray-100 shadow-md'
            } rounded-lg p-8 text-center flex flex-col items-center relative`}
          >
            {e.destacado && (
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                ¡NUEVO!
              </div>
            )}
            <img
              src={e.img}
              alt={e.title}
              className="w-full h-60 object-cover rounded-md mb-6 border border-blue-100"
            />
            <h3 className={`text-2xl font-bold mb-3 ${e.destacado ? 'text-blue-800' : 'text-blue-700'}`}>
              {e.title}
            </h3>
            <div className="mb-3 space-y-2">
              {e.ponente && (
                <div>
                  <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    Ponente: {e.ponente}
                  </span>
                </div>
              )}
              {e.modalidad && (
                <div>
                  <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    Modalidad: {e.modalidad}
                  </span>
                </div>
              )}
            </div>
            {e.desc && (
              <p className="text-lg text-gray-600 mb-3">{e.desc}</p>
            )}
            <span className="text-base text-gray-400">{e.date}</span>
            {e.destacado && (
              <div className="mt-4">
                <span className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-green-700 transition-colors cursor-pointer">
                  Más info en WhatsApp
                </span>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// --- Call to Action (Join) Section ---
const Join = () => (
  <section id="join" className="py-24 bg-blue-700 text-white text-center">
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="text-3xl md:text-4xl font-bold mb-6"
    >
      ¿Listo para impulsar tu futuro con datos?
    </motion.h2>
    <motion.a
      href="https://docs.google.com/forms/d/e/1FAIpQLSet5SPOboLaO5nRJ6XUf5uADwXvyErTL-o1n7h1_HYc1shqeA/viewform?usp=dialog"
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="inline-block px-10 py-4 bg-white text-blue-700 rounded-full font-semibold text-lg transition-colors hover:bg-blue-50 shadow-lg hover:shadow-xl"
    >
      ¡Inscríbete Ya!
    </motion.a>
    <p className="mt-4 text-white/80 text-lg">Las inscripciones para nuevos miembros ya están <b>ABIERTAS</b>.</p>
    <p className="mt-2 text-white/70 text-base">¿Eres miembro actual? Mantente informado a través de nuestra comunidad de WhatsApp.</p>
  </section>
);

// --- Merch Section ---
const merchItems = [
  {
    name: 'Camiseta Data Drivers',
    desc: 'Camiseta oficial del club, perfecta para eventos y el día a día.',
    img: require('./images/merch1.png'),
  },
  {
    name: 'Taza Data Drivers',
    desc: 'Empieza tu día con energía y estilo Data Drivers.',
    img: require('./images/merch2.png'),
  },
  // Puedes añadir más productos aquí
];

const wallpapers = [
  {
    nombre: 'Fondo iPhone 0',
    archivo: 'wallpaper0.png',
    tipo: 'iPhone',
  },
  {
    nombre: 'Fondo iPhone 1',
    archivo: 'wallpaper1.png',
    tipo: 'iPhone',
  },
  {
    nombre: 'Fondo iPhone 2',
    archivo: 'wallpaper2.png',
    tipo: 'iPhone',
  },
  {
    nombre: 'Fondo iPhone 3',
    archivo: 'wallpaper3.png',
    tipo: 'iPhone',
  },
  {
    nombre: 'Fondo iPhone 4',
    archivo: 'wallpaper4.png',
    tipo: 'iPhone',
  },
  {
    nombre: 'Fondo PC 1',
    archivo: 'wallpaperpc1.png',
    tipo: 'PC',
  },
  {
    nombre: 'Fondo PC 2',
    archivo: 'wallpaperpc2.png',
    tipo: 'PC',
  },
];

const FondosPantalla = () => (
  <section id="fondos" className="py-8 bg-white">
    <div className="max-w-5xl mx-auto px-4">
      <h2 className="text-2xl font-semibold text-gray-900 mb-8 text-center tracking-tight">Fondos de Pantalla</h2>
      <p className="text-base text-gray-500 text-center mb-12">Descarga fondos exclusivos de Data Drivers para tu móvil o PC. Haz clic en el botón para obtener la imagen en máxima calidad.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
        {wallpapers.map((w, i) => (
          <div key={i} className="flex flex-col items-center">
            <img
              src={require(`./images/${w.archivo}`)}
              alt={w.nombre}
              className="w-full max-w-2xl h-[32rem] object-cover rounded-3xl mb-8"
            />
            <span className="text-xs text-gray-400 mb-4">{w.tipo}</span>
            <a
              href={require(`./images/${w.archivo}`)}
              download={w.nombre.replace(/ /g, '_')}
              className="px-10 py-3 bg-black text-white rounded-full text-base font-semibold hover:bg-gray-800 transition"
            >
              Descargar
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Merch = () => (
  <section id="merch" className="py-24 bg-white">
    <div className="max-w-5xl mx-auto px-4">
      <h1 className="text-4xl md:text-5xl font-extrabold text-blue-700 text-center mb-8 drop-shadow-lg tracking-tight">Recursos y Merchandising</h1>
      
      {/* Aviso merchandising físico */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-xl mb-8 text-justify text-base font-medium max-w-2xl mx-auto">
        El merchandising físico de Data Drivers estará disponible en el futuro. ¡Permanece atento a las novedades! Por ahora puedes disfrutar de los fondos de pantalla o la playlist.
      </div>

      {/* Recursos digitales */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4 text-center">Recursos Digitales</h2>
        
        {/* Playlist de Spotify */}
        <div className="mb-6 flex justify-center">
          <iframe
            src="https://open.spotify.com/embed/playlist/2H4IJIpCSkrkcC86JO68m8?utm_source=generator&theme=0&si=m1LLb4lMQZCcOpWkN3rrVw"
            width="100%"
            height="380"
            style={{ maxWidth: '600px', borderRadius: '12px', border: 'none', display: 'block', margin: '0 auto' }}
            frameBorder="0"
            allowfullscreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Playlist Data Drivers"
          ></iframe>
        </div>
        <div className="mb-6 text-justify text-base text-gray-700 max-w-2xl mx-auto">
          Disfruta de la playlist oficial de Data Drivers en Spotify, seleccionada por y para los miembros del club. ¡Ideal para programar, estudiar o inspirarte!
        </div>
      </div>
      {/* ...existing code... */}
      {/* Si se quieren dejar fotos, muy pequeñas y difuminadas, si no, eliminar este bloque */}
      {/*
      <div className="flex justify-center gap-6 mb-10">
        {merchItems.map((item, i) => (
          <img
            key={item.name}
            src={item.img}
            alt={item.name}
            className="w-16 h-16 object-contain rounded-xl opacity-30 blur-sm"
            style={{filter: 'blur(2px)'}}
          />
        ))}
      </div>
      */}
    </div>
  </section>
);

// --- Location Section ---
const Location = () => (
  <section id="ubicacion" className="py-24 bg-gradient-to-b from-blue-50 via-white to-white border-t border-b border-gray-100">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">Ubicación</h2>
      <p className="text-lg text-gray-700 mb-10">Estamos en la <b>Facultad de Económicas</b> de la Universidad de Navarra, Pamplona.</p>
      <div className="mx-auto max-w-xl rounded-3xl shadow-xl border border-blue-200 bg-white overflow-hidden">
        <iframe
          title="Facultad de Económicas - Universidad de Navarra"
          src="https://www.google.com/maps?q=Facultad+de+Econ%C3%B3micas,+Universidad+de+Navarra,+Pamplona&output=embed"
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </div>
      <img
        src={require('./images/unav.png')}
        alt="Universidad de Navarra Logo"
        className="w-40 h-40 object-contain mx-auto mt-6"
      />
    </div>
  </section>
);

// --- Footer ---
const Footer = () => {
  // Detectar si estamos en la home
  const isHome = window.location.pathname === '/';
  // Función para scroll suave o redirección
  const handleNewsletterClick = (e) => {
    e.preventDefault();
    if (isHome) {
      scroller.scrollTo('newsletter-teaser', { smooth: true, duration: 700, offset: -80 });
    } else {
      window.location.href = '/#newsletter-teaser';
    }
  };
  return (
    <footer className="py-8 bg-white border-t border-gray-100 text-center text-gray-500 text-sm">
      <div className="flex flex-col md:flex-row items-center justify-center gap-4">
        <span>Contacto: <a href="mailto:datadriversunav@gmail.com" className="text-blue-700 hover:underline">datadriversunav@gmail.com</a></span>
        <span className="hidden md:inline">|</span>
        <span className="flex gap-2 items-center justify-center">
          <a href="https://www.linkedin.com/company/data-drivers-unav/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            LinkedIn
          </a>
          <a href="https://www.instagram.com/datadriversunav" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            Instagram
          </a>
          <a href="https://chat.whatsapp.com/your-invite-link" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-500 text-white font-medium hover:bg-green-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor" className="w-4 h-4"><path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.832 4.584 2.236 6.37L4 29l7.824-2.05A11.94 11.94 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22c-1.77 0-3.47-.46-4.95-1.33l-.35-.21-4.65 1.22 1.24-4.53-.23-.36A9.94 9.94 0 016 15c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10zm5.29-7.71c-.29-.15-1.71-.84-1.98-.94-.27-.1-.47-.15-.67.15-.2.29-.77.94-.95 1.13-.17.2-.35.22-.64.07-.29-.15-1.22-.45-2.33-1.43-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.35.44-.53.15-.18.2-.31.3-.51.1-.2.05-.38-.02-.53-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51-.17-.01-.37-.01-.57-.01-.2 0-.53.08-.81.38-.28.3-1.07 1.05-1.07 2.56 0 1.5 1.09 2.95 1.24 3.16.15.2 2.14 3.28 5.19 4.47.73.31 1.3.5 1.75.64.74.24 1.41.21 1.94.13.59-.09 1.81-.74 2.07-1.45.26-.71.26-1.32.18-1.45-.08-.13-.26-.2-.55-.35z"/></svg>
            WhatsApp
          </a>
          <a
            href="#newsletter-teaser"
            onClick={handleNewsletterClick}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Newsletter
          </a>
        </span>
        <span className="hidden md:inline">|</span>
        <span>&copy; {new Date().getFullYear()} Data Drivers | UNAV</span>
      </div>
      <div className="mt-4 text-xs text-gray-400 max-w-2xl mx-auto">
        Data Drivers es un club universitario de la Universidad de Navarra. El uso, descarga o difusión de cualquier imagen, dato o información de esta web está estrictamente prohibido salvo autorización expresa del club.
      </div>
    </footer>
  );
};

// --- Merch Page ---
const MerchPage = () => (
  <div className="font-sans bg-white min-h-screen">
    <Navbar />
    <main>
      <Merch />
      <FondosPantalla />
    </main>
    <Footer />
  </div>
);

// --- Colaboradores Section ---
const collaborators = [
  {
    name: 'Microsoft',
    img: require('./images/microsoft.png'),
    desc: 'Colaboración en formación y eventos de tecnología.'
  },
  {
    name: 'Datacamp',
    img: require('./images/datacamp.png'),
    desc: 'Patrocinio y recursos para formación en data science.'
  },
  {
    name: 'Denodo',
    img: require('./images/denodo.png'),
    desc: 'Colaboración en workshops y master classes.'
  },
  {
    name: 'IBM',
    img: require('./images/ibm.png'),
    desc: 'Charlas, ponencias y apoyo en IA y mainframe.'
  },
  // Puedes añadir más empresas aquí
];

const Colaboradores = () => (
  <section id="colaboradores" className="py-20 bg-white border-t border-b border-gray-100">
    <div className="max-w-5xl mx-auto px-6 text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-10">Empresas Colaboradoras</h2>
      <p className="text-lg text-gray-700 mb-10">Estas son algunas de las empresas con las que hemos contado para apoyarnos, impartir ponencias y colaborar en actividades del club.</p>
      <div className="flex flex-col md:flex-row gap-8 justify-center items-center">
        {collaborators.map((c) => (
          <div key={c.name} className="bg-gray-50 rounded-xl border border-gray-200 p-6 flex flex-col items-center w-full md:w-72 shadow-sm">
            <img src={c.img} alt={c.name} className="w-24 h-24 object-contain mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{c.name}</h3>
            <p className="text-gray-600 text-center text-base">{c.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- About Us Page ---
const destacadosAsistencia = [
  { name: 'Constanza', value: '100%' },
  { name: 'Marta', value: '94%' },
  { name: 'Beñat', value: '88%' },
  { name: 'Agustin', value: '88%' },
  { name: 'Alba', value: '88%' },
  { name: 'Sebastián', value: '82%' },
];
const destacadosDataCamp = [
  { name: 'Diego', value: '93.635 XP' },
  { name: 'Hanna', value: '82.350 XP' },
  { name: 'Alejandro', value: '71.251 XP' },
  { name: 'Leonardo', value: '65.008 XP' },
  { name: 'Fernando', value: '39.950 XP' },
];

const PodiumRow = ({ idx, name, value }) => {
  let rowClass = '';
  if (idx === 0) rowClass = 'bg-yellow-100 font-bold';
  else if (idx === 1) rowClass = 'bg-gray-100 font-semibold';
  else if (idx === 2) rowClass = 'bg-orange-100 font-semibold';
  return (
    <tr className={`transition-colors hover:bg-blue-50 ${rowClass}`}> 
      <td className="px-4 py-2">{idx + 1}</td>
      <td className="px-4 py-2">{name}</td>
      <td className="px-4 py-2">{value}</td>
    </tr>
  );
};

const AboutUsPage = () => (
  <div className="font-sans bg-white min-h-screen">
    <Navbar />
    <main className="max-w-3xl mx-auto px-6 py-20">
      <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-700 via-blue-400 to-blue-700 text-center mb-10 drop-shadow-lg tracking-tight">
        Fundadores & Visión
      </h1>
      <div className="flex flex-col items-center mb-12">
        <img src={require('./images/IMG_0285(1).JPG')} alt="Fundadores Data Drivers" className="w-full max-w-2xl rounded-3xl object-cover border-4 border-blue-100 mb-4 shadow-2xl" style={{ minHeight: '320px', maxHeight: '480px' }} />
        <span className="text-gray-600 text-lg mt-2 text-center">Miguel, Dante y Marco — Fundadores de Data Drivers</span>
        <p className="text-lg text-gray-700 mt-8 text-center max-w-2xl">
          Nuestra visión es crear una comunidad universitaria vibrante y colaborativa, donde los estudiantes puedan aprender, compartir y crecer juntos en el mundo de los datos y la tecnología. Creemos en el poder de la comunidad para impulsar el talento, la innovación y el futuro profesional de cada miembro.
        </p>
      </div>
      <section className="mb-12 bg-gradient-to-br from-yellow-50 via-white to-blue-50 rounded-3xl border-2 border-yellow-300 shadow-lg p-8 flex flex-col items-center">
        <FaTrophy className="w-16 h-16 text-yellow-400 mb-2 drop-shadow" />
        <h2 className="text-3xl md:text-4xl font-extrabold text-yellow-700 mb-2 text-center tracking-tight">Miembros Destacados 24-25</h2>
        <p className="text-lg text-blue-700 mb-6 text-center font-medium">Reconocemos el esfuerzo, la constancia y la pasión por los datos.<br />¡Enhorabuena a los más comprometidos!</p>
        <div className="w-full grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold text-blue-700 mb-2">Asistencia</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-700">#</th>
                    <th className="px-4 py-2 text-left text-gray-700">Nombre</th>
                    <th className="px-4 py-2 text-left text-gray-700">Asistencia</th>
                  </tr>
                </thead>
                <tbody>
                  {destacadosAsistencia.map((m, idx) => (
                    <PodiumRow key={m.name} idx={idx} name={m.name} value={m.value} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-blue-700 mb-2">DataCamp</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-700">#</th>
                    <th className="px-4 py-2 text-left text-gray-700">Nombre</th>
                    <th className="px-4 py-2 text-left text-gray-700">XP</th>
                  </tr>
                </thead>
                <tbody>
                  {destacadosDataCamp.map((m, idx) => (
                    <PodiumRow key={m.name} idx={idx} name={m.name} value={m.value} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

// --- Vaquero Section ---
const VaqueroSection = () => (
  <section className="relative w-full py-20 bg-black flex flex-col items-center justify-center">
    <h2 className="text-2xl md:text-3xl font-semibold text-white drop-shadow-lg text-center mb-4 z-10 relative">
      Espíritu Data Drivers
    </h2>
    <h3 className="text-lg text-white/90 mb-8 text-center max-w-2xl z-10 relative">
      "Construir comunidad, compartir conocimiento y crecer juntos"
    </h3>
    <div className="relative w-full flex justify-center">
      <img
        src={require('./images/vaquero.png')}
        alt="Espíritu Data Drivers"
        className="w-full max-h-[500px] object-cover object-center grayscale"
        style={{ opacity: 0.85 }}
      />
    </div>
  </section>
);

// --- Testimonios Section ---
const testimonios = [
  {
    nombre: 'Hanna',
    frase: 'En el club he aprendido mucho de programación, incluso desde cero. Siempre hay alguien dispuesto a ayudar.',
    avatar: true,
  },
  {
    nombre: 'Constanza',
    frase: 'El evento del último día con comida fue una gran oportunidad para conocer a todos y compartir experiencias.',
    avatar: true,
  },
  {
    nombre: 'Sebastián',
    frase: 'Me parece genial que el club colabore con empresas, así podemos ver cómo se aplican los datos en el mundo real.',
    avatar: true,
  },
];

const Testimonios = () => (
  <section id="testimonios" className="py-20 bg-gradient-to-b from-blue-50 via-white to-white border-t border-b border-blue-100">
    <div className="max-w-5xl mx-auto px-6 text-center">
      <h2 className="text-3xl font-bold text-blue-700 mb-10">Testimonios de Miembros</h2>
      <div className="grid md:grid-cols-3 gap-8">
        {testimonios.map((t, i) => (
          <motion.div
            key={t.nombre}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15, duration: 0.7, type: 'spring', bounce: 0.3 }}
            className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center border border-blue-100 hover:scale-105 hover:shadow-2xl transition-transform duration-300"
          >
            {t.avatar ? (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 border-4 border-blue-200 mb-4 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12 text-white">
                  <path fillRule="evenodd" d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" clipRule="evenodd" />
                </svg>
              </div>
            ) : (
              <img src={t.img} alt={t.nombre} className="w-24 h-24 rounded-full object-cover border-4 border-blue-200 mb-4" />
            )}
            <p className="text-lg text-gray-700 italic mb-2">“{t.frase}”</p>
            <span className="font-bold text-blue-700">{t.nombre}</span>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// --- Chatbot DD ---
function getSaludoHora() {
  const h = new Date().getHours();
  if (h < 7) return '¡Hola noctámbulo! 🌙';
  if (h < 13) return '¡Buenos días! ☀️';
  if (h < 20) return '¡Buenas tardes!';
  return '¡Buenas noches!';
}

const DD_BOT_RESPONSES = [
  // Saludo contextual
  { match: /^(hola|buenas|hey|holi|hello)$/i, resp: () => getSaludoHora() + ' Soy DD. ¿Listo para descubrir el club más innovador de la UNAV?' },
  { match: /buenos días|buenas tardes|buenas noches/i, resp: () => getSaludoHora() + ' ¡Qué alegría verte por aquí! ¿En qué puedo ayudarte con Data Drivers?' },
  { match: /gracias|thank|thanks/i, resp: '¡De nada! Para eso estoy, para ayudarte a surfear el mundo de los datos 🚀.' },
  { match: /adios|bye|hasta luego|nos vemos/i, resp: '¡Nos vemos! Que tengas un día lleno de datos increíbles 😎' },
  { match: /perfecto|genial|excelente|bien/i, resp: '¡Me alegra saber que te gusta! ¿Hay algo más en lo que pueda ayudarte?' },
  { match: /cómo estás|qué tal|estás bien/i, resp: '¡Estoy súper bien, gracias por preguntar! Estoy aquí para ayudarte con todo lo relacionado con Data Drivers. ¿Y tú cómo estás?' },
  { match: /sí|no|vale|ok|okay/i, resp: 'Perfecto, ¿hay algo más en lo que pueda ayudarte?' },
  { match: /no sé|no tengo idea|ayúdame/i, resp: '¡No te preocupes! Estoy aquí para ayudarte. ¿Sobre qué tema específico necesitas información?' },

  // Conceptos teóricos de IA y tecnología
  { match: /qué es la ia|inteligencia artificial|definir ia/i, resp: 'La Inteligencia Artificial (IA) es la capacidad de las máquinas para realizar tareas que normalmente requieren inteligencia humana: reconocer patrones, aprender, razonar y tomar decisiones. ¡En Data Drivers exploramos sus aplicaciones prácticas!' },
  { match: /machine learning|ml|aprendizaje automático/i, resp: 'Machine Learning es un subcampo de la IA donde las máquinas aprenden de datos sin programación explícita. Usa algoritmos para encontrar patrones y hacer predicciones. ¡Perfecto para análisis de datos!' },
  { match: /python|programación|programar/i, resp: 'Python es un lenguaje de programación súper popular para datos e IA. Es fácil de aprender, tiene librerías increíbles como pandas, numpy, scikit-learn. ¡En el club te ayudamos a dominarlo!' },
  { match: /datos|data|análisis/i, resp: 'Los datos son la materia prima del futuro. El análisis de datos permite extraer insights valiosos, tomar decisiones informadas y crear soluciones innovadoras. ¡Es el superpoder que desarrollamos en Data Drivers!' },
  { match: /algoritmo|modelo|predicción/i, resp: 'Los algoritmos son como recetas para que las máquinas aprendan. Los modelos son versiones entrenadas que pueden hacer predicciones. ¡En el club aprendemos a crearlos y usarlos!' },
  { match: /rag|retrieval augmented generation/i, resp: 'RAG es una técnica de IA que combina búsqueda de información con generación de texto. Primero busca datos relevantes, luego genera respuestas basadas en esa información. ¡Tenemos un evento sobre esto pronto!' },
  { match: /pandas|numpy|matplotlib|scikit/i, resp: '¡Excelentes librerías de Python! Pandas para manipular datos, NumPy para cálculos numéricos, Matplotlib para visualizaciones y Scikit-learn para machine learning. ¡Las dominamos en Data Drivers!' },
  { match: /visualización|gráfico|plot|chart/i, resp: 'La visualización de datos es clave para comunicar insights. Usamos herramientas como Matplotlib, Seaborn, Plotly para crear gráficos que cuenten historias con datos. ¡Es todo un arte!' },
  { match: /deep learning|redes neuronales|neural/i, resp: 'Deep Learning usa redes neuronales artificiales inspiradas en el cerebro humano. Puede reconocer imágenes, procesar lenguaje natural y hacer predicciones complejas. ¡Una de las áreas más emocionantes de la IA!' },
  { match: /chatgpt|gpt|openai/i, resp: 'GPT y ChatGPT son modelos de IA generativa que pueden crear texto, conversar y responder preguntas. Usan técnicas como transformer y se entrenan con enormes cantidades de datos. ¡Fascinante tecnología!' },

  // Sobre el club y lo que se hace
  { match: /evento|actividades|próximos|pasados/i, resp: { text: 'Puedes ver todos los eventos y actividades aquí.', action: 'scroll', target: 'eventos' } },
  { match: /aprender|contenido|qué hacéis|temas/i, resp: 'En el club exploramos análisis de datos, Python, R, visualización, IA, machine learning y más. Mira la sección "Qué hacemos".' },
  { match: /beneficio|ventaja|por qué/i, resp: { text: 'Consulta los beneficios aquí.', action: 'scroll', target: 'memberbenefits' } },
  { match: /crédito|ects|convalid/i, resp: '¡Sí! Participando activamente puedes convalidar ECTS cada semestre. Mira los beneficios.' },
  { match: /empleo|trabajo|salidas|carrera|futuro/i, resp: 'El club te conecta con empresas, proyectos reales, y oportunidades como hackathones y datathons. ¡Un boost a tu CV!' },
  { match: /colabora|empresas|externo|patrocina/i, resp: 'Colaboramos con empresas como IBM, Celonis y otras startups para que vivas experiencias reales. Más detalles en la sección "Partners".' },
  { match: /material|grabaciones|recursos|videos/i, resp: 'Algunos workshops tienen grabaciones disponibles. Están en la sección "Recursos" o escríbenos si no encuentras lo que buscas.' },
  { match: /premio|datathon|ganasteis|logros/i, resp: '¡Sí! Hemos ganado datathones como el Celonis Datathon y participado en eventos internacionales. Consulta "Logros del Club".' },

  // Participación y comunidad
  { match: /inscrip|apuntar|unir|formulario|alta/i, resp: 'Para nuevos estudiantes: haz clic en "Join" en el menú o ve a la sección de inscripción en home donde encontrarás el formulario. Si ya eres miembro actual, mantente informado a través de nuestra comunidad de WhatsApp.' },
  { match: /nuevo estudiante|nuevo miembro|primera vez/i, resp: '¡Bienvenido! Los nuevos estudiantes deben usar el formulario de inscripción. Haz clic en "Join" en el menú o busca la sección de inscripción en home. El plazo está abierto hasta octubre 2025.' },
  { match: /miembro actual|antiguo miembro|ya soy/i, resp: 'Si ya eres miembro de Data Drivers, mantente informado sobre eventos y actividades a través de nuestra comunidad de WhatsApp. ¡No necesitas volver a inscribirte!' },
  { match: /quién puede|requisitos|perfil|estudio/i, resp: 'Cualquier estudiante de UNAV interesado en datos puede unirse: ADE, Ingeniería, ISSA, Medicina… ¡La diversidad suma!' },
  { match: /horario|cuándo|reuniones|quedadas/i, resp: 'Nos reunimos cada dos semanas los miércoles a las 14:00 en el aula 14 del Edificio de Económicas. ¡Pero revisa la sección "Eventos" por si hay cambios!' },

  // Sobre el equipo y ubicación
  { match: /fundador|quién|lead|líder|founder/i, resp: 'Miguel, Dante y Marco son los fundadores y líderes del club. Cada uno aporta su talento: Miguel lidera la visión, Dante es el mago de la automatización y Marco es el estratega de los datathones. ¡Juntos forman el dream team de Data Drivers!' },
  { match: /miguel|dante|marco/i, resp: '¿Uno de los líderes? ¡Los cracks del club! Miguel te inspira a ir más allá, Dante automatiza hasta el café y Marco ya está resolviendo el siguiente datathon antes de que empiece 😎.' },
  { match: /ubicación|dónde|localización|mapa/i, resp: 'Estamos en la Facultad de Económicas, Universidad de Navarra. Mira la sección de ubicación para el mapa.' },

  // Contacto y ayuda
  { match: /contacto|email|correo/i, resp: 'Puedes escribirnos a datadriversunav@gmail.com o por LinkedIn/WhatsApp (en el pie de página).' },
  { match: /join|botón join|sección join/i, resp: 'El botón "Join" te lleva a la sección de inscripción donde encontrarás el formulario para nuevos miembros. Si ya eres miembro, usa WhatsApp para estar al día.' },
  { match: /formulario|google forms|docs.google/i, resp: 'El formulario está en la sección "Join" de la página home. Es para nuevos estudiantes únicamente. Miembros actuales: usad WhatsApp.' },
  { match: /plazo|octubre|hasta cuándo/i, resp: 'El plazo de inscripción está abierto hasta octubre 2025. ¡No te quedes fuera! Nuevos estudiantes: formulario en sección "Join". Miembros actuales: WhatsApp.' },
  { match: /no se|no sabes|ayuda|perdido|duda/i, resp: '¡No te preocupes! Explora el menú o pregúntame por cualquier sección. ¡Aquí nadie se queda atrás!' },
  { match: /eres|quién eres|qué eres/i, resp: '¡Soy DD! Tu asistente digital del club Data Drivers. Estoy aquí para ayudarte a sacar el máximo partido al club 💡' },

  // Fallback para entradas no reconocidas
  { match: /.*/, resp: 'Mmm... eso no lo tengo aún, pero puedes explorar el menú de la web o escribirme con otras palabras 😊' }
];

function DDChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: '¡Hola! Soy DD, tu asistente. ¿En qué puedo ayudarte?' }
  ]);

  function handleSend() {
    if (!input.trim()) return;
    const userMsg = { from: 'user', text: input };
    const found = DD_BOT_RESPONSES.find(r => r.match.test(input));
    let botMsg;
    if (found) {
      if (typeof found.resp === 'function') {
        botMsg = { from: 'bot', text: found.resp() };
      } else if (typeof found.resp === 'object' && found.resp.action === 'scroll') {
        botMsg = { from: 'bot', text: found.resp.text, action: found.resp.action, target: found.resp.target };
      } else {
        botMsg = { from: 'bot', text: found.resp };
      }
    } else {
      botMsg = { from: 'bot', text: '¡No tengo la respuesta exacta, pero explora el menú o pregúntame por cualquier sección. ¡Aquí nadie se queda sin ayuda! 🚀' };
    }
    setMessages(msgs => [...msgs, userMsg, botMsg]);
    setInput('');
    // Si hay acción de scroll
    if (botMsg.action === 'scroll' && botMsg.target) {
      setTimeout(() => {
        const el = document.getElementById(botMsg.target);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSend();
  }

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed z-50 bottom-6 right-6 bg-white rounded-full shadow-lg p-2 border-2 border-blue-300 hover:scale-105 transition-transform"
        style={{ width: 64, height: 64 }}
        aria-label="Abrir chat DD"
      >
        <img src={DDImg} alt="Mascota DD" className="w-full h-full object-contain" />
      </button>
      {open && (
        <div className="fixed z-50 bottom-24 right-6 w-80 max-w-xs bg-white rounded-2xl shadow-2xl border-2 border-blue-200 flex flex-col overflow-hidden animate-fade-in">
          <div className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2">
            <img src={DDImg} alt="Mascota DD" className="w-8 h-8 rounded-full border border-white" />
            <span className="font-bold">DD Asistente</span>
          </div>
          <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-64 bg-blue-50">
            {messages.map((msg, i) => (
              <div key={i} className={msg.from === 'bot' ? 'text-left' : 'text-right'}>
                <span className={msg.from === 'bot' ? 'inline-block bg-white text-blue-700 rounded-lg px-3 py-1 mb-1 shadow' : 'inline-block bg-blue-600 text-white rounded-lg px-3 py-1 mb-1 shadow'}>
                  {msg.text}
                </span>
              </div>
            ))}
          </div>
          <div className="flex border-t border-blue-100">
            <input
              className="flex-1 px-3 py-2 outline-none text-sm"
              placeholder="Escribe tu pregunta..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <button
              onClick={handleSend}
              className="px-4 text-blue-700 font-bold hover:text-blue-900"
              aria-label="Enviar"
            >
              &rarr;
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// --- Newsletter Teaser (Home) ---
const NewsletterTeaser = ({ onOpen }) => {
  // Buscar la newsletter más reciente por fecha
  const newsletter = newsletters.reduce((latest, curr) => new Date(curr.fecha) > new Date(latest.fecha) ? curr : latest, newsletters[0]);
  return (
    <section id="newsletter-teaser" className="py-16 bg-white border-t border-b border-blue-100">
      <div className="max-w-3xl mx-auto px-6 flex flex-col md:flex-row items-center gap-8">
        {newsletter.portada && (
          <img src={require(`./images/${newsletter.portada}`)} alt="Portada newsletter" className="w-40 h-40 object-cover rounded-2xl border-2 border-blue-200 shadow-md" />
        )}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-blue-800 mb-2">{newsletter.titulo}</h2>
          <span className="text-sm text-gray-500 mb-2 block">{new Date(newsletter.fecha).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          <p className="text-base text-gray-700 mb-4 line-clamp-2">{newsletter.resumen}</p>
          <button
            onClick={onOpen}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-700 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors shadow"
          >
            Leer más <FaShareAlt />
          </button>
        </div>
      </div>
    </section>
  );
};

// --- NewsletterPage y modal mejorado ---
const NewsletterPage = () => {
  const [selected, setSelected] = useState(null);
  // Filtrar solo enero y recap junio
  let filteredNewsletters = newsletters.filter(nl => {
    const fecha = new Date(nl.fecha);
    // Enero de cualquier año
    const isEnero = fecha.getMonth() === 0;
    // Recap de junio 2025
    const isRecap = nl.fecha === '2025-06-01';
    return isEnero || isRecap;
  });
  // Ordenar por fecha descendente (más reciente primero)
  filteredNewsletters = filteredNewsletters.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  // Compartir
  function handleCopyLink() {
    navigator.clipboard.writeText(window.location.origin + '/newsletter');
  }
  function handleShareWhatsApp() {
    window.open(`https://wa.me/?text=Lee%20la%20newsletter%20de%20Data%20Drivers%20Club%20UNAV:%20${encodeURIComponent(window.location.origin + '/newsletter')}`);
  }
  return (
    <div className="font-sans bg-white min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-20">
        <h1 className="text-4xl md:text-5xl font-extrabold text-blue-700 text-center mb-10 drop-shadow-lg tracking-tight">Newsletter</h1>
        <p className="text-lg text-gray-700 text-center mb-8">Consulta aquí la última newsletter y el recap anual del club.</p>
        <div className="space-y-10">
          {filteredNewsletters.map((nl, idx) => (
            <button
              key={idx}
              className="w-full text-left bg-blue-50 rounded-2xl border-2 border-blue-100 shadow p-6 hover:bg-blue-100 transition-colors"
              onClick={() => setSelected(nl)}
              aria-label={`Abrir ${nl.titulo}`}
            >
              <h2 className="text-2xl font-bold text-blue-800 mb-1">{nl.titulo}</h2>
              <span className="text-sm text-gray-500 mb-2 block">{new Date(nl.fecha).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <p className="text-base text-gray-700 mb-2 line-clamp-2">{nl.resumen}</p>
              <span className="text-blue-700 font-medium underline">Leer completa</span>
            </button>
          ))}
        </div>
        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl border-2 border-blue-200 max-w-lg w-full p-8 relative animate-zoom-in">
              <button
                className="absolute top-3 right-3 text-blue-700 text-2xl font-bold hover:text-blue-900"
                onClick={() => setSelected(null)}
                aria-label="Cerrar newsletter"
              >
                ×
              </button>
              {/* Mostrar portada SIEMPRE si existe, también en el recap */}
              {selected.portada && (
                <img
                  src={require(`./images/${selected.portada}`)}
                  alt="Portada newsletter"
                  className={
                    selected.fecha === '2025-06-01'
                      ? 'w-full max-h-96 object-contain mb-4 border border-blue-100 bg-white rounded-xl'
                      : 'w-full h-40 object-cover rounded-xl mb-4 border border-blue-100'
                  }
                  style={selected.fecha === '2025-06-01' ? { background: 'white' } : {}}
                />
              )}
              {/* Si hay foto principal y NO es recap, mostrarla debajo de la portada */}
              {selected.foto && selected.fecha !== '2025-06-01' && (
                <img src={selected.foto} alt="Imagen noticia principal" className="w-full h-40 object-cover rounded-xl mb-4 border border-blue-100" />
              )}
              <h2 className="text-3xl font-bold text-blue-800 mb-1">{selected.titulo}</h2>
              <span className="text-sm text-gray-500 mb-4 block">{new Date(selected.fecha).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <p className="text-base text-gray-700 mb-4">{selected.resumen}</p>
              <ul className="list-disc list-inside space-y-3 mb-6">
                {selected.noticias && selected.noticias.map((item, idx) => (
                  <li key={idx}>
                    <a href={item.enlace} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline font-medium">{item.texto}</a>
                  </li>
                ))}
              </ul>
              {/* No mostrar PDF recap ni botón de descarga */}
              {/* Protección extra: si es recap, no mostrar nada de PDF */}
              {selected.fecha === '2025-06-01' && null}
              <div className="flex items-center justify-between mt-6">
                {selected.autor && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FaUserCircle className="w-5 h-5 text-blue-400" />
                    <span>Autor: {selected.autor}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <button onClick={handleCopyLink} className="text-blue-700 hover:text-blue-900" title="Copiar enlace"><FaShareAlt className="w-5 h-5" /></button>
                  <button onClick={handleShareWhatsApp} className="text-blue-700 hover:text-blue-900" title="Compartir en WhatsApp"><FaWhatsapp className="w-5 h-5" /></button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

// --- Panel de administración sencillo (solo frontend, genera JSON) ---
const AdminNewsletter = () => {
  const [form, setForm] = useState({
    titulo: '', fecha: '', autor: '', genero: 'hombre', portada: '', resumen: '', noticias: [{ texto: '', enlace: '' }]
  });
  const [json, setJson] = useState('');
  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }
  function handleNoticiaChange(idx, field, value) {
    const noticias = [...form.noticias];
    noticias[idx][field] = value;
    setForm({ ...form, noticias });
  }
  function addNoticia() {
    setForm({ ...form, noticias: [...form.noticias, { texto: '', enlace: '' }] });
  }
  function removeNoticia(idx) {
    setForm({ ...form, noticias: form.noticias.filter((_, i) => i !== idx) });
  }
  function handleSubmit(e) {
    e.preventDefault();
    setJson(JSON.stringify([form], null, 2));
  }
  return (
    <div className="font-sans bg-white min-h-screen">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-20">
        <h1 className="text-3xl font-bold text-blue-700 mb-8">Panel de Administración de Newsletter</h1>
        <form onSubmit={handleSubmit} className="space-y-6 bg-blue-50 p-6 rounded-xl border border-blue-100">
          <input name="titulo" value={form.titulo} onChange={handleChange} placeholder="Título" className="w-full p-2 rounded border" required />
          <input name="fecha" value={form.fecha} onChange={handleChange} placeholder="Fecha (YYYY-MM-DD)" className="w-full p-2 rounded border" required />
          <input name="autor" value={form.autor} onChange={handleChange} placeholder="Autor" className="w-full p-2 rounded border" required />
          <select name="genero" value={form.genero} onChange={handleChange} className="w-full p-2 rounded border">
            <option value="hombre">Hombre</option>
            <option value="mujer">Mujer</option>
          </select>
          <input name="portada" value={form.portada} onChange={handleChange} placeholder="Nombre de la imagen de portada (opcional)" className="w-full p-2 rounded border" />
          <textarea name="resumen" value={form.resumen} onChange={handleChange} placeholder="Resumen" className="w-full p-2 rounded border" required />
          <div className="space-y-3">
            {form.noticias.map((n, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input value={n.texto} onChange={e => handleNoticiaChange(idx, 'texto', e.target.value)} placeholder="Titular de la noticia" className="flex-1 p-2 rounded border" required />
                <input value={n.enlace} onChange={e => handleNoticiaChange(idx, 'enlace', e.target.value)} placeholder="Enlace" className="flex-1 p-2 rounded border" required />
                <button type="button" onClick={() => removeNoticia(idx)} className="text-red-500 font-bold">×</button>
              </div>
            ))}
            <button type="button" onClick={addNoticia} className="text-blue-700 font-bold">+ Añadir noticia</button>
          </div>
          <button type="submit" className="bg-blue-700 text-white px-6 py-2 rounded font-semibold hover:bg-blue-800">Generar JSON</button>
        </form>
        {json && (
          <div className="mt-8">
            <h2 className="text-lg font-bold mb-2">Copia este JSON en newsletters.json:</h2>
            <pre className="bg-gray-100 p-4 rounded text-xs overflow-x-auto border border-gray-200">{json}</pre>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

// --- Main App ---
function App() {
  return (
    <Router>
      <Helmet>
        <title>Data Drivers Club UNAV</title>
        <meta name="description" content="Club universitario de datos, IA y tecnología en la Universidad de Navarra. Workshops, eventos, networking y más." />
        <meta property="og:title" content="Data Drivers Club UNAV" />
        <meta property="og:description" content="Club universitario de datos, IA y tecnología en la Universidad de Navarra. Workshops, eventos, networking y más." />
        <meta property="og:image" content="/logo192.png" />
        <meta property="og:type" content="website" />
      </Helmet>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={
          <div className="font-sans bg-white min-h-screen">
            <Navbar />
            <InscriptionNotification />
            <main>
              <Hero />
              <PromotionalBanner />
              <Features />
              <MemberBenefits />
              <Colaboradores />
              <Events />
              <Testimonios />
              <Join />
              <VaqueroSection />
              <NewsletterTeaser onOpen={() => window.location.href='/newsletter'} />
              <Gallery />
              <Location />
            </main>
            <Footer />
            <DDChatbot />
          </div>
        } />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/newsletter" element={<NewsletterPage />} />
        <Route path="/admin-newsletter" element={<AdminNewsletter />} />
      </Routes>
    </Router>
  );
}

export default App;
